import { type FilterDefinition } from "@/shared/components/ResourceView";
import { metricGroupByFieldKind } from "@/shared/domain/metrics";

/**
 * The filter types a custom field can be grouped by: the discrete ones.
 *
 * `numeric` and `date` custom fields (Number / Timestamp on the wire) are
 * continuous, so grouping by one would draw a series per distinct value.
 */
const GROUPABLE_CUSTOM_FIELD_TYPES: ReadonlySet<FilterDefinition["type"]> =
  new Set(["string", "enum", "boolean"]);

/**
 * Whether a chart card can be grouped by this filter definition: its path is
 * one `metricGroupByFieldKind` accepts, and a custom field's type is one of
 * the discrete ones.
 *
 * The rule sits beside the dashboard's own filter list, not as a flag on the
 * shared {@link FilterDefinition}, because every resource grid in the app
 * uses that type and only this dashboard's Group by picker groups by one.
 */
export function isGroupable(definition: FilterDefinition): boolean {
  switch (metricGroupByFieldKind(definition.field)) {
    case "native":
      return true;
    case "custom":
      return GROUPABLE_CUSTOM_FIELD_TYPES.has(definition.type);
    case undefined:
      return false;
  }
}

/**
 * The Group by picker's options, DERIVED from the same `FilterDefinition[]`
 * the dashboard's filter menu is built from.
 *
 * Derived rather than declared, so a custom field added to an org appears in
 * both menus at once and one place on the client decides what is groupable.
 * The definitions are passed through untouched — the picker is the same
 * `FieldPicker` the filter menu uses, and reads the same `name`, `type` and
 * `icon` off them — and in the order they arrived, which the filter menu has
 * already sorted (see `sortByFieldToken`).
 */
export function groupableFieldDefinitions(
  definitions: FilterDefinition[],
): FilterDefinition[] {
  return definitions.filter(isGroupable);
}
